import { useState } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { Row, Col } from "antd";
import "../../Styles/styles-TeamData.css";
import "../../Styles/styles-PostData.css";
import { useSelector, useDispatch } from "react-redux";
import {
	setTeamPresets,
	setNutritionStatDefaults,
} from "../../store/actions/team.actions";
import { LoadingComponent } from "../Common/LoadingComponent";
import { CompactStatTile } from "../StatTileV2/CompactStatTile.jsx";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js";
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";
import { ForceplateAndTestingBreakdownTable } from "../Team/ForceplateAndTestingBreakdownTable.jsx";
import { MultiMetricChart } from "../MultMetricChart/MultiMetricChart.jsx";
import { OptionGroupSelect } from "../Compare/Components_1v1/OptionGroupSelect";
import { useNutritionMetrics } from "./useNutritionMetrics";
import { audioMessage as message } from "../Common/Notifications/audioMessage";

const trainingAPI = new TrainingAPI();

function Nutrition({ type, ...props }) {
	const PRESET_TYPE = PresetType.Nutrition;
	const DEFAULT_PRESET = "Default";
	const dispatch = useDispatch();


	const [statMetrics, setStatMetrics] = useState([]);
	const [option, setOption] = useState("latest_vs_prev");
	const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET);
	const [unsavedChanges, setUnsavedChanges] = useState(false);

	const {
		nutritionStatDefaults,
		positions,
		playerGroups: groups,
		allPlayers,
		all_metric_types,
		players: playerData,
		misc: { comparison_types },
		info: { team_colors: teamColors },
		filter: { player_group, session },
		dropdowns: { nutrition: nutritionDropdowns },
		presets,
	} = useSelector(({ team }) => team);

	const {
		loading: { nutrition: nutritionLoading },
		filter: { endDate, player, startDate },
	} = useSelector((store) => store[type]);

	const { user } = useAuth0();

	useMetricDropdowns(["nutrition"]);

	// Use the custom hook to manage nutrition-related metrics processing.
	const {
		playerPosGroupOptionsSelected,
		playerPosGroupOptions,
		processedStatDataList,
		processedDataNonEmpty,
		handleOptionGroupLeftChange,
	} = useNutritionMetrics({
		startDate,
		endDate,
		session,
		playerData,
		groups,
		allPlayers,
		positions,
		statMetrics,
		metricType: "nutrition",
		all_metric_types,
		type,
	});

	const handleSaveMetrics = async () => {
		const filtered = statMetrics.filter((val) => !!val);
		try {
			if (currentSelectedTilePreset === DEFAULT_PRESET) {
				await trainingAPI.updateDefaultStats(filtered, "nutrition");
				dispatch(setNutritionStatDefaults(filtered));
			} else {
				const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset);
				await trainingAPI.updatePreset({
					name: currentSelectedTilePreset,
					type: PRESET_TYPE,
					config: filtered,
					preset_id: presetModified.preset_id,
				});
				presetModified.config = filtered;
				dispatch(setTeamPresets(presets));
			}
			message.success("Saved!");
		} catch (e) {
			console.error(e);
		}
	};

	const handleChangeMetric = (index) => (key) => {
		setStatMetrics((prevStatMetrics) => {
			const newStatMetrics = [...prevStatMetrics];
			newStatMetrics[index] = key;
			return newStatMetrics;
		});
		setUnsavedChanges(true);
	};

	let playerDisplayName = "";
	if (type !== "team" && type !== "player") return null;
	if (type === "player") {
		let singlePlayer = allPlayers.find((x) => x.player_id === player)
		if (singlePlayer) {
			const singlePlayerFirstName = singlePlayer?.first_name ? singlePlayer.first_name : "";
			const singlePlayerLastName = singlePlayer?.last_name ? singlePlayer.last_name : "";
			playerDisplayName = singlePlayerFirstName + " " + singlePlayerLastName;
		}
	}

	return (
		<LoadingComponent loading={nutritionLoading}>
			<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
				<div className="flex flex-col justify-center items-center w-full" id="statistics-widget">
					<Row gutter={16} style={{ width: "80%", marginBottom: 16, justifyContent: "center", gap: "10px" }}>
						<PresetSelection
							presets={presets}
							user={user}
							presetType={PRESET_TYPE}
							statDefaults={nutritionStatDefaults}
							currStatMetrics={statMetrics}
							setStatMetrics={setStatMetrics}
							currentSelectedTilePreset={currentSelectedTilePreset}
							setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
							comparisonTypes={comparison_types}
							comparisonOption={option}
							setComparisonOption={setOption}
							handleSave={handleSaveMetrics}
							warnUnsavedChanges={unsavedChanges}
							setWarnUnsavedChages={setUnsavedChanges}
						/>
					</Row>
					<Row gutter={16} style={{ width: "90%", marginBottom: 16 }} className="exportable tab-nutrition nutrition-stats">
						{statMetrics.concat(new Array(4).fill(undefined)).slice(0, 4).map((metric, i) => (
							<Col span={6} key={i} className={`${props.metric_type}-stat-tile`}>
								<div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
									<CompactStatTile
										dropdowns={nutritionDropdowns}
										metric_identifier_with_stat={metric}
										onChange={handleChangeMetric(i)}
										option={option}
										endDate={endDate}
										startDate={startDate}
										isCompact={true}
										type={type}
										comparison={option}
										{...(type === "player" && { playerId: player })}
										positionFilter={props.position_filter}
									/>
								</div>
							</Col>
						))}
					</Row>
				</div>
				{type === "team" && (
					<div style={{ width: "80%", marginBottom: 16 }} className="player-group-pos-container">
						<OptionGroupSelect
							className="player-group-pos-left"
							selectedOptionVal={playerPosGroupOptionsSelected}
							options={playerPosGroupOptions}
							placeholder="Full Team"
							width={500}
							sideId={""}
							handleOptionGroupChange={handleOptionGroupLeftChange}
						/>
					</div>
				)}
				<Row gutter={16} style={{ width: "90%" }}>
					{type === "player" ? (
						<Col lg={24} xl={24} key={0}>
							<MultiMetricChart
								values={statMetrics.concat(new Array(4).fill(undefined)).slice(0, 4)}
								metricType="nutrition"
								startDate={startDate}
								endDate={endDate}
								forceTitle={playerDisplayName}
								{...(type === "player" && { playerId: player })}
								type={type}
								position={props.position_filter}
								colors={teamColors.all}
								showDataLabelToggle={true}
								player_group={player_group}
								sessions={session}
								chartIndex={0}
								pageType="player"
							/>
						</Col>
					) : null}
					{type === "team" &&
						processedStatDataList.length > 0 &&
						processedStatDataList.map((data, idx) => (
							<Col lg={24} xl={24} key={`nutrition-chart-cols-${idx}`}>
								<MultiMetricChart
									values={statMetrics.concat(new Array(4).fill(undefined)).slice(0, 4)}
									metricType="nutrition"
									startDate={startDate}
									endDate={endDate}
									{...(type === "team" && { playerId: player })}
									type={type}
									position={props.position_filter}
									usePreloadedData={true}
									preComputedData={data}
									preComputedPlayerDataIsNotEmpty={processedDataNonEmpty[idx]}
									forceTitle={data.name}
									colors={teamColors.all}
									showDataLabelToggle={true}
									player_group={player_group}
									sessions={session}
									chartIndex={idx}
									pageType="team"
								/>
							</Col>
						))}
				</Row>
				{type === "team" && (
					<ForceplateAndTestingBreakdownTable
						showAll={props.showAll}
						type={type}
						metric_type="nutrition"
						start_date={startDate}
						end_date={endDate}
						position_filter={props.position_filter}
						stat="Avg"
					/>
				)}
			</div>
		</LoadingComponent>
	);
}

export default Nutrition;
